import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Paperclip, Trash2, Save, Edit2, X, Plus, Mail, Inbox,
  FileText, User, Clock, ChevronRight, Search, Filter, MoreVertical,
  Copy, RefreshCw, File, Download, Eye, Check, AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  de: {
    mailbox: "E-Mail-Center",
    compose: "Neue E-Mail",
    drafts: "Entwürfe",
    sent: "Gesendet",
    templates: "Vorlagen",
    archived: "Archiviert",
    to: "An",
    cc: "CC",
    bcc: "BCC",
    subject: "Betreff",
    message: "Nachricht",
    send: "Senden",
    saveDraft: "Als Entwurf speichern",
    attachments: "Anhänge",
    addAttachment: "Datei anhängen",
    selectClient: "Kunde auswählen...",
    selectProject: "Projekt auswählen...",
    selectTemplate: "Vorlage verwenden",
    noTemplates: "Keine Vorlagen",
    noDrafts: "Keine Entwürfe",
    noSent: "Keine gesendeten E-Mails",
    noArchived: "Keine archivierten E-Mails",
    delete: "Löschen",
    edit: "Bearbeiten",
    preview: "Vorschau",
    archive: "Archivieren",
    unarchive: "Wiederherstellen",
    sentAt: "Gesendet am",
    sentBy: "Gesendet von",
    loading: "Laden...",
    emailSent: "E-Mail gesendet!",
    draftSaved: "Entwurf gespeichert!",
    emailArchived: "E-Mail archiviert!",
    emailUnarchived: "E-Mail wiederhergestellt!",
    configureSmtp: "Bitte konfigurieren Sie zuerst die SMTP-Einstellungen",
    newTemplate: "Neue Vorlage",
    templateName: "Vorlagenname",
    category: "Kategorie",
    save: "Speichern",
    cancel: "Abbrechen",
    general: "Allgemein",
    welcome: "Willkommen",
    project: "Projekt",
    invoice: "Rechnung",
    reminder: "Erinnerung",
    back: "Zurück",
    placeholders: "Verfügbare Platzhalter: {name}, {project}, {amount}, {date}",
    recipient: "Empfänger",
    linkedTo: "Verknüpft mit",
    signature: "Signatur",
    addSignature: "Signatur hinzufügen",
    editSignature: "Signatur bearbeiten",
    signatureEnabled: "Signatur aktiv",
    signatureDisabled: "Ohne Signatur",
    signaturePlaceholder: "z.B. Mit freundlichen Grüßen,\nIhr Name\nMahzod Media Group",
    signatureSaved: "Signatur gespeichert"
  },
  en: {
    mailbox: "Email Center",
    compose: "New Email",
    drafts: "Drafts",
    sent: "Sent",
    templates: "Templates",
    archived: "Archived",
    to: "To",
    cc: "CC",
    bcc: "BCC",
    subject: "Subject",
    message: "Message",
    send: "Send",
    saveDraft: "Save as draft",
    attachments: "Attachments",
    addAttachment: "Add attachment",
    selectClient: "Select client...",
    selectProject: "Select project...",
    selectTemplate: "Use template",
    noTemplates: "No templates",
    noDrafts: "No drafts",
    noSent: "No sent emails",
    noArchived: "No archived emails",
    delete: "Delete",
    edit: "Edit",
    preview: "Preview",
    archive: "Archive",
    unarchive: "Unarchive",
    sentAt: "Sent at",
    sentBy: "Sent by",
    loading: "Loading...",
    emailSent: "Email sent!",
    draftSaved: "Draft saved!",
    emailArchived: "Email archived!",
    emailUnarchived: "Email unarchived!",
    configureSmtp: "Please configure SMTP settings first",
    newTemplate: "New Template",
    templateName: "Template name",
    category: "Category",
    save: "Save",
    cancel: "Cancel",
    general: "General",
    welcome: "Welcome",
    project: "Project",
    invoice: "Invoice",
    reminder: "Reminder",
    back: "Back",
    placeholders: "Available placeholders: {name}, {project}, {amount}, {date}",
    recipient: "Recipient",
    linkedTo: "Linked to",
    signature: "Signature",
    addSignature: "Add signature",
    editSignature: "Edit signature",
    signatureEnabled: "Signature active",
    signatureDisabled: "No signature",
    signaturePlaceholder: "e.g. Best regards,\nYour Name\nMahzod Media Group",
    signatureSaved: "Signature saved"
  }
};

// Rich Text Editor (simplified) - Fixed cursor jumping issue
const RichTextEditor = ({ value, onChange, placeholder, lang }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  // Set initial content only once
  useEffect(() => {
    if (editorRef.current && !isInitialized.current && value) {
      editorRef.current.innerHTML = value;
      isInitialized.current = true;
    }
  }, []);

  // Update content when value changes from outside (e.g., template selection)
  useEffect(() => {
    if (editorRef.current && isInitialized.current) {
      // Only update if the value is significantly different (from template)
      const currentContent = editorRef.current.innerHTML;
      if (value && value !== currentContent && value.length > 0 && currentContent.length === 0) {
        editorRef.current.innerHTML = value;
      }
    }
  }, [value]);

  const execCommand = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  const handleInput = (e) => {
    const content = e.currentTarget.innerHTML;
    onChange(content);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="border border-mmg-mine-shaft">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-mmg-mine-shaft bg-mmg-cod-gray">
        <button type="button" onClick={() => execCommand("bold")} className="p-2 text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft" title="Bold">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => execCommand("italic")} className="p-2 text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft" title="Italic">
          <em>I</em>
        </button>
        <button type="button" onClick={() => execCommand("underline")} className="p-2 text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft" title="Underline">
          <u>U</u>
        </button>
        <span className="w-px h-6 bg-mmg-mine-shaft mx-1" />
        <button type="button" onClick={() => execCommand("insertUnorderedList")} className="p-2 text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft" title="Bullet List">
          •
        </button>
        <button type="button" onClick={() => execCommand("insertOrderedList")} className="p-2 text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft" title="Numbered List">
          1.
        </button>
        <span className="w-px h-6 bg-mmg-mine-shaft mx-1" />
        <button type="button" onClick={() => {
          const url = prompt("Link URL:");
          if (url) execCommand("createLink", url);
        }} className="p-2 text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft" title="Add Link">
          🔗
        </button>
      </div>
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[200px] p-4 bg-black text-white font-body text-sm focus:outline-none"
        style={{ lineHeight: 1.6 }}
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

// Compose Email Component
const ComposeEmail = ({ token, lang, clients, projects, templates, onClose, editDraft, onSuccess }) => {
  const t = translations[lang] || translations.en;
  const fileRef = useRef(null);
  const editorRef = useRef(null);
  const [form, setForm] = useState({
    to_email: editDraft?.to_email || "",
    to_name: editDraft?.to_name || "",
    subject: editDraft?.subject || "",
    body_html: editDraft?.body_html || "",
    cc: editDraft?.cc || "",
    bcc: editDraft?.bcc || "",
    attachments: editDraft?.attachments || [],
    client_id: editDraft?.client_id || "",
    project_id: editDraft?.project_id || ""
  });
  const [attachmentNames, setAttachmentNames] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(!!editDraft?.cc);
  const [showBcc, setShowBcc] = useState(!!editDraft?.bcc);
  const [editorKey, setEditorKey] = useState(0);
  const [useSignature, setUseSignature] = useState(true);  // Optional signature
  const [signature, setSignature] = useState("");
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);

  // Load saved signature on mount
  useEffect(() => {
    const savedSig = localStorage.getItem(`email_signature_${lang}`) || "";
    setSignature(savedSig);
  }, [lang]);

  const saveSignature = () => {
    localStorage.setItem(`email_signature_${lang}`, signature);
    setShowSignatureEditor(false);
    toast.success(t.signatureSaved);
  };

  // Auto-fill from client
  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setForm({
        ...form,
        client_id: clientId,
        to_email: client.email,
        to_name: client.username || client.company || ""
      });
    }
  };

  // Apply template - force editor re-render
  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const newBody = template.body_html?.[lang] || template.body_html?.de || "";
      setForm({
        ...form,
        subject: template.subject?.[lang] || template.subject?.de || "",
        body_html: newBody
      });
      // Force editor to re-initialize with new content
      setEditorKey(prev => prev + 1);
    }
  };

  // Upload attachment
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/mailbox/upload-attachment`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setForm({ ...form, attachments: [...form.attachments, res.data.url] });
      setAttachmentNames([...attachmentNames, res.data.filename]);
      toast.success(lang === "de" ? "Datei hochgeladen" : "File uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAttachment = (index) => {
    const newAtt = [...form.attachments];
    const newNames = [...attachmentNames];
    newAtt.splice(index, 1);
    newNames.splice(index, 1);
    setForm({ ...form, attachments: newAtt });
    setAttachmentNames(newNames);
  };

  // Send email
  const handleSend = async () => {
    if (!form.to_email || !form.subject) {
      toast.error(lang === "de" ? "Empfänger und Betreff erforderlich" : "Recipient and subject required");
      return;
    }
    setSending(true);
    try {
      // Include language and optional signature in the request
      const emailData = { 
        ...form, 
        lang,
        signature: useSignature && signature ? signature : null
      };
      await axios.post(`${API}/mailbox/send`, emailData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.emailSent);
      if (editDraft?.id) {
        await axios.delete(`${API}/mailbox/drafts/${editDraft.id}`, { headers: { Authorization: `Bearer ${token}` } });
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  // Save as draft
  const handleSaveDraft = async () => {
    setSending(true);
    try {
      if (editDraft?.id) {
        await axios.put(`${API}/mailbox/drafts/${editDraft.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/mailbox/drafts`, form, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t.draftSaved);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error("Failed to save draft");
    }
    setSending(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white">
          <ArrowLeft size={18} />{t.back}
        </button>
        <h2 className="font-heading font-bold text-2xl text-white">{t.compose}</h2>
        <div className="w-24" />
      </div>

      {/* Quick selectors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-mmg-silver-chalice mb-1">{t.selectClient}</label>
          <select value={form.client_id} onChange={(e) => handleClientSelect(e.target.value)}
            className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
            <option value="">{t.selectClient}</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.username || c.email} {c.company ? `(${c.company})` : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-mmg-silver-chalice mb-1">{t.selectProject}</label>
          <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
            className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
            <option value="">{t.selectProject}</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-mmg-silver-chalice mb-1">{t.selectTemplate}</label>
          <select onChange={(e) => handleTemplateSelect(e.target.value)}
            className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
            <option value="">{t.selectTemplate}</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>

      {/* Email form */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        {/* To */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-xs text-mmg-silver-chalice mb-1">{t.to}*</label>
            <input type="email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })}
              placeholder="email@example.com" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" required />
          </div>
          <div className="w-48">
            <label className="block text-xs text-mmg-silver-chalice mb-1">Name</label>
            <input type="text" value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })}
              placeholder="Max Mustermann" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
          <div className="flex flex-col justify-end gap-1">
            {!showCc && <button type="button" onClick={() => setShowCc(true)} className="text-xs text-mmg-silver-chalice hover:text-white">+CC</button>}
            {!showBcc && <button type="button" onClick={() => setShowBcc(true)} className="text-xs text-mmg-silver-chalice hover:text-white">+BCC</button>}
          </div>
        </div>

        {/* CC */}
        {showCc && (
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.cc}</label>
              <input type="text" value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })}
                placeholder="email1@example.com, email2@example.com" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            </div>
            <button type="button" onClick={() => { setShowCc(false); setForm({ ...form, cc: "" }); }} className="p-2 text-mmg-silver-chalice hover:text-white">
              <X size={16} />
            </button>
          </div>
        )}

        {/* BCC */}
        {showBcc && (
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.bcc}</label>
              <input type="text" value={form.bcc} onChange={(e) => setForm({ ...form, bcc: e.target.value })}
                placeholder="email@example.com" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            </div>
            <button type="button" onClick={() => { setShowBcc(false); setForm({ ...form, bcc: "" }); }} className="p-2 text-mmg-silver-chalice hover:text-white">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Subject */}
        <div>
          <label className="block text-xs text-mmg-silver-chalice mb-1">{t.subject}*</label>
          <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" required />
        </div>

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs text-mmg-silver-chalice">{t.message}</label>
            <p className="text-xs text-mmg-boulder italic">{t.placeholders}</p>
          </div>
          <RichTextEditor 
            key={editorKey}
            value={form.body_html} 
            onChange={(v) => setForm({ ...form, body_html: v })} 
            placeholder={t.message} 
            lang={lang} 
          />
        </div>

        {/* Attachments */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-mmg-silver-chalice">{t.attachments}</label>
            <button type="button" onClick={() => fileRef.current?.click()}
              className="text-xs text-mmg-silver-chalice hover:text-white flex items-center gap-1">
              <Paperclip size={12} />{t.addAttachment}
            </button>
            <input ref={fileRef} type="file" onChange={handleFileUpload} className="hidden" />
          </div>
          {form.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.attachments.map((att, i) => (
                <div key={att} className="flex items-center gap-2 px-3 py-1 bg-mmg-mine-shaft text-sm text-white">
                  <File size={12} />
                  <span>{attachmentNames[i] || att.split("/").pop()}</span>
                  <button type="button" onClick={() => removeAttachment(i)} className="text-mmg-silver-chalice hover:text-red-500">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Signature - Optional */}
        <div className="border-t border-mmg-mine-shaft pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <label className="text-xs text-mmg-silver-chalice">{t.signature}</label>
              <button
                type="button"
                onClick={() => setUseSignature(!useSignature)}
                data-testid="toggle-signature-btn"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${useSignature ? 'bg-white' : 'bg-mmg-mine-shaft'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full transition-transform ${useSignature ? 'translate-x-5 bg-black' : 'translate-x-1 bg-mmg-silver-chalice'}`} />
              </button>
              <span className="text-xs text-mmg-silver-chalice">{useSignature ? t.signatureEnabled : t.signatureDisabled}</span>
            </div>
            {useSignature && (
              <button type="button" onClick={() => setShowSignatureEditor(!showSignatureEditor)}
                className="text-xs text-mmg-silver-chalice hover:text-white flex items-center gap-1"
                data-testid="edit-signature-btn">
                <Edit2 size={12} />{signature ? t.editSignature : t.addSignature}
              </button>
            )}
          </div>
          {useSignature && showSignatureEditor && (
            <div className="space-y-2">
              <textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={t.signaturePlaceholder}
                rows={4}
                data-testid="signature-textarea"
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white text-sm font-body resize-none"
              />
              <div className="flex gap-2">
                <button type="button" onClick={saveSignature}
                  className="text-xs px-3 py-1.5 bg-mmg-mine-shaft text-white hover:bg-white hover:text-black transition-colors"
                  data-testid="save-signature-btn">
                  <Check size={12} className="inline mr-1" />{t.save}
                </button>
                <button type="button" onClick={() => setShowSignatureEditor(false)}
                  className="text-xs px-3 py-1.5 text-mmg-silver-chalice hover:text-white">
                  {t.cancel}
                </button>
              </div>
            </div>
          )}
          {useSignature && signature && !showSignatureEditor && (
            <div className="px-4 py-3 bg-black/50 border border-mmg-mine-shaft/50 text-sm text-mmg-silver-chalice whitespace-pre-line" data-testid="signature-preview">
              {signature}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <button onClick={handleSend} disabled={sending}
          className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50 flex items-center gap-2"
          data-testid="send-email-btn">
          <Send size={16} />{sending ? "..." : t.send}
        </button>
        <button onClick={handleSaveDraft} disabled={sending}
          className="px-6 py-3 bg-transparent text-white font-heading font-bold uppercase hover:bg-mmg-mine-shaft border border-mmg-mine-shaft disabled:opacity-50 flex items-center gap-2"
          data-testid="save-draft-btn">
          <Save size={16} />{t.saveDraft}
        </button>
      </div>
    </div>
  );
};

// Main Mailbox Component
const Mailbox = ({ token, lang }) => {
  const t = translations[lang] || translations.en;
  const [activeTab, setActiveTab] = useState("compose"); // compose, drafts, sent, archived, templates
  const [drafts, setDrafts] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [archivedEmails, setArchivedEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDraft, setEditDraft] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [draftsRes, sentRes, archivedRes, templatesRes, clientsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/mailbox/drafts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mailbox/sent`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mailbox/sent/archived`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mailbox/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDrafts(draftsRes.data);
      setSentEmails(sentRes.data);
      setArchivedEmails(archivedRes.data);
      setTemplates(templatesRes.data);
      setClients(clientsRes.data.filter(u => u.role === "client"));
      setProjects(projectsRes.data);
    } catch (e) { }
    setLoading(false);
  };

  const handleDeleteDraft = async (id) => {
    if (!window.confirm(lang === "de" ? "Entwurf löschen?" : "Delete draft?")) return;
    try {
      await axios.delete(`${API}/mailbox/drafts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  const handleArchiveEmail = async (id) => {
    try {
      await axios.patch(`${API}/mailbox/sent/${id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.emailArchived);
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  const handleUnarchiveEmail = async (id) => {
    try {
      await axios.patch(`${API}/mailbox/sent/${id}/unarchive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.emailUnarchived);
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  const handleDeleteArchivedEmail = async (id) => {
    if (!window.confirm(lang === "de" ? "E-Mail endgültig löschen?" : "Delete email permanently?")) return;
    try {
      await axios.delete(`${API}/mailbox/sent/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "E-Mail gelöscht" : "Email deleted");
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  const handleExportEmailPdf = async (email) => {
    try {
      const response = await axios.get(`${API}/mailbox/sent/${email.id}/export-pdf`, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Email_${email.subject.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date(email.sent_at).toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(lang === "de" ? "PDF exportiert" : "PDF exported");
    } catch (e) { 
      toast.error(lang === "de" ? "Export fehlgeschlagen" : "Export failed"); 
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm(lang === "de" ? "Vorlage löschen?" : "Delete template?")) return;
    try {
      await axios.delete(`${API}/mailbox/templates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  const handleSaveTemplate = async () => {
    if (!editTemplate.name) {
      toast.error(lang === "de" ? "Name erforderlich" : "Name required");
      return;
    }
    try {
      if (editTemplate.id) {
        await axios.put(`${API}/mailbox/templates/${editTemplate.id}`, editTemplate, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/mailbox/templates`, editTemplate, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(lang === "de" ? "Vorlage gespeichert" : "Template saved");
      setEditTemplate(null);
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  // Compose view
  if (activeTab === "compose" || editDraft) {
    return (
      <ComposeEmail
        token={token}
        lang={lang}
        clients={clients}
        projects={projects}
        templates={templates}
        editDraft={editDraft}
        onClose={() => { setEditDraft(null); setActiveTab("drafts"); }}
        onSuccess={fetchData}
      />
    );
  }

  // Email detail view
  if (selectedEmail) {
    return (
      <div className="space-y-6">
        <button onClick={() => setSelectedEmail(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white">
          <ArrowLeft size={18} />{t.back}
        </button>
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-mmg-mine-shaft pb-4">
            <div>
              <h2 className="font-heading text-xl text-white">{selectedEmail.subject}</h2>
              <p className="text-sm text-mmg-silver-chalice">{t.to}: {selectedEmail.to_name ? `${selectedEmail.to_name} <${selectedEmail.to_email}>` : selectedEmail.to_email}</p>
            </div>
            <div className="text-right text-sm text-mmg-silver-chalice">
              <p>{t.sentAt}: {new Date(selectedEmail.sent_at).toLocaleString("de-DE")}</p>
              <p>{t.sentBy}: {selectedEmail.sent_by_name}</p>
            </div>
          </div>
          <div className="prose prose-invert max-w-none text-white" dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
          {selectedEmail.attachments?.length > 0 && (
            <div className="pt-4 border-t border-mmg-mine-shaft">
              <p className="text-xs text-mmg-silver-chalice mb-2">{t.attachments}:</p>
              <div className="flex flex-wrap gap-2">
                {selectedEmail.attachments.map((att) => (
                  <a key={att} href={`${BACKEND_URL}${att}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1 bg-mmg-mine-shaft text-sm text-white hover:bg-white hover:text-black transition-colors">
                    <File size={12} />{att.split("/").pop()}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Template edit view
  if (editTemplate) {
    return (
      <div className="space-y-6">
        <button onClick={() => setEditTemplate(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white">
          <ArrowLeft size={18} />{t.back}
        </button>
        <h2 className="font-heading font-bold text-2xl text-white">{editTemplate.id ? t.edit : t.newTemplate}</h2>
        
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.templateName}*</label>
              <input type="text" value={editTemplate.name} onChange={(e) => setEditTemplate({ ...editTemplate, name: e.target.value })}
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.category}</label>
              <select value={editTemplate.category} onChange={(e) => setEditTemplate({ ...editTemplate, category: e.target.value })}
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white">
                <option value="general">{t.general}</option>
                <option value="welcome">{t.welcome}</option>
                <option value="project">{t.project}</option>
                <option value="invoice">{t.invoice}</option>
                <option value="reminder">{t.reminder}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{t.subject} (DE)</label>
            <input type="text" value={editTemplate.subject?.de || ""} onChange={(e) => setEditTemplate({ ...editTemplate, subject: { ...editTemplate.subject, de: e.target.value } })}
              className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{t.subject} (EN)</label>
            <input type="text" value={editTemplate.subject?.en || ""} onChange={(e) => setEditTemplate({ ...editTemplate, subject: { ...editTemplate.subject, en: e.target.value } })}
              className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>

          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{t.message} (DE)</label>
            <textarea value={editTemplate.body_html?.de || ""} onChange={(e) => setEditTemplate({ ...editTemplate, body_html: { ...editTemplate.body_html, de: e.target.value } })}
              rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{t.message} (EN)</label>
            <textarea value={editTemplate.body_html?.en || ""} onChange={(e) => setEditTemplate({ ...editTemplate, body_html: { ...editTemplate.body_html, en: e.target.value } })}
              rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
          </div>

          <p className="text-xs text-mmg-silver-chalice">{t.placeholders}</p>
        </div>

        <div className="flex gap-4">
          <button onClick={handleSaveTemplate}
            className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"
            data-testid="save-template-btn">
            <Save size={16} className="inline mr-2" />{t.save}
          </button>
          <button onClick={() => setEditTemplate(null)}
            className="px-6 py-3 bg-transparent text-white font-heading font-bold uppercase hover:bg-mmg-mine-shaft border border-mmg-mine-shaft">
            {t.cancel}
          </button>
        </div>
      </div>
    );
  }

  // Main view with tabs
  return (
    <div className="space-y-6" data-testid="mailbox-container">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white flex items-center gap-3">
          <Mail size={28} />{t.mailbox}
        </h1>
        <button onClick={() => setActiveTab("compose")}
          className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white flex items-center gap-2"
          data-testid="new-email-btn">
          <Plus size={14} />{t.compose}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-mmg-mine-shaft">
        {[
          { key: "drafts", label: t.drafts, icon: FileText, count: drafts.length },
          { key: "sent", label: t.sent, icon: Send, count: sentEmails.length },
          { key: "archived", label: t.archived, icon: Inbox, count: archivedEmails.length },
          { key: "templates", label: t.templates, icon: Copy, count: templates.length }
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-6 py-3 font-heading text-sm uppercase tracking-widest transition-colors border-b-2 -mb-px ${
              activeTab === tab.key ? "text-white border-white" : "text-mmg-silver-chalice border-transparent hover:text-white"
            }`}
            data-testid={`tab-${tab.key}`}>
            <tab.icon size={16} />
            {tab.label}
            {tab.count > 0 && <span className="ml-1 px-2 py-0.5 bg-mmg-mine-shaft text-xs">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="min-h-[400px]">
        {/* Drafts */}
        {activeTab === "drafts" && (
          drafts.length === 0 ? (
            <div className="text-center py-16 text-mmg-silver-chalice">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t.noDrafts}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {drafts.map(draft => (
                <div key={draft.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex items-center justify-between hover:border-white/30 transition-colors">
                  <div className="flex-1 min-w-0" onClick={() => setEditDraft(draft)} style={{ cursor: "pointer" }}>
                    <p className="font-heading text-white truncate">{draft.subject || "(Kein Betreff)"}</p>
                    <p className="text-sm text-mmg-silver-chalice truncate">{draft.to_email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-mmg-silver-chalice">{new Date(draft.updated_at).toLocaleDateString("de-DE")}</span>
                    <button onClick={() => setEditDraft(draft)} className="p-2 text-mmg-silver-chalice hover:text-white" data-testid={`edit-draft-${draft.id}`}>
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDeleteDraft(draft.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-draft-${draft.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Sent */}
        {activeTab === "sent" && (
          sentEmails.length === 0 ? (
            <div className="text-center py-16 text-mmg-silver-chalice">
              <Send size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t.noSent}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sentEmails.map(email => (
                <div key={email.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex items-center justify-between hover:border-white/30 transition-colors">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelectedEmail(email)}>
                    <p className="font-heading text-white truncate">{email.subject}</p>
                    <p className="text-sm text-mmg-silver-chalice truncate">{t.to}: {email.to_name || email.to_email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-mmg-silver-chalice">{new Date(email.sent_at).toLocaleDateString("de-DE")}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleArchiveEmail(email.id); }}
                      className="text-mmg-silver-chalice hover:text-white p-2"
                      title={t.archive}>
                      <Inbox size={16} />
                    </button>
                    <Check size={16} className="text-green-500" />
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Archived */}
        {activeTab === "archived" && (
          archivedEmails.length === 0 ? (
            <div className="text-center py-16 text-mmg-silver-chalice">
              <Inbox size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t.noArchived}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {archivedEmails.map(email => (
                <div key={email.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex items-center justify-between hover:border-white/30 transition-colors">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelectedEmail(email)}>
                    <p className="font-heading text-white truncate">{email.subject}</p>
                    <p className="text-sm text-mmg-silver-chalice truncate">{t.to}: {email.to_name || email.to_email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-mmg-silver-chalice mr-2">{new Date(email.sent_at).toLocaleDateString("de-DE")}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleExportEmailPdf(email); }}
                      className="text-mmg-silver-chalice hover:text-white p-2"
                      title={t.exportPdf || "Als PDF exportieren"}>
                      <Download size={16} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleUnarchiveEmail(email.id); }}
                      className="text-mmg-silver-chalice hover:text-white p-2"
                      title={t.unarchive}>
                      <RefreshCw size={16} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleDeleteArchivedEmail(email.id); }}
                      className="text-red-400 hover:text-red-300 p-2"
                      title={t.delete || "Löschen"}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Templates */}
        {activeTab === "templates" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setEditTemplate({ name: "", subject: { de: "", en: "" }, body_html: { de: "", en: "" }, category: "general" })}
                className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white flex items-center gap-2"
                data-testid="new-template-btn">
                <Plus size={14} />{t.newTemplate}
              </button>
            </div>
            {templates.length === 0 ? (
              <div className="text-center py-16 text-mmg-silver-chalice">
                <Copy size={48} className="mx-auto mb-4 opacity-50" />
                <p>{t.noTemplates}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map(template => (
                  <div key={template.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-white">{template.name}</h3>
                      <span className="text-xs px-2 py-1 bg-mmg-mine-shaft text-mmg-silver-chalice uppercase">{template.category}</span>
                    </div>
                    <p className="text-sm text-mmg-silver-chalice truncate">{template.subject?.de || template.subject?.en}</p>
                    <div className="flex gap-2 pt-2">
                      <button onClick={() => setEditTemplate(template)} className="text-xs text-mmg-silver-chalice hover:text-white flex items-center gap-1">
                        <Edit2 size={12} />{t.edit}
                      </button>
                      <button onClick={() => handleDeleteTemplate(template.id)} className="text-xs text-mmg-silver-chalice hover:text-red-500 flex items-center gap-1">
                        <Trash2 size={12} />{t.delete}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Mailbox;
